import {
  STUDIO_GLOBAL_NAV_SECTIONS,
  STUDIO_ROUTE_REGISTRY,
  type StudioNavigationSection,
} from './studio-route-registry'
import type { StudioRoute } from '@/store/types'

/**
 * One destination row of the ⌘K command palette.
 *
 * `section` names the nav group the row was derived from, or is `null` for the
 * rows that have no nav group at all — the ones the shell labels with the
 * route's own name instead of a section heading.
 */
export interface StudioCommandDestination {
  route: StudioRoute
  section: StudioNavigationSection | null
}

/**
 * Every route the command palette can navigate to on THIS build.
 *
 * Extracted from `studio-dashboard-shell.tsx`'s `COMMAND_DESTINATIONS`
 * `useMemo` (wave 6), which still consumes it — the shell only adds the
 * localized `category` label on top. It was extracted because it could not be
 * asserted where it was: for a `privileged` route the palette is the ONLY
 * in-UI way to reach the screen, and a `useMemo` in a component body is not
 * reachable from a gate.
 *
 * WHY THAT MATTERS HERE. `admin` has no nav-rail entry on either distribution
 * (`STUDIO_GLOBAL_NAV_SECTIONS` admits only `navigationVisibility: 'global'`),
 * so the hand-written clause below is the whole of its in-UI reachability.
 * Measured 2026-08-18 by the controller: renaming the `'admin'` literal in
 * that clause — deleting the appliance identity console's only route into the
 * UI — left 7 files / 98 tests GREEN. Nothing observed it. The wave-6 lane had
 * just spent its whole scope making that console reachable again, and a
 * one-token rename would have silently undone it.
 *
 * So the gate is derived from HERE, the code that actually builds the list:
 * `src/lib/__tests__/studio-appliance-admin-surface.test.tsx` asserts that
 * `admin` appears among these routes on BOTH distributions, alongside a
 * `settings` control row that must survive the same mutation.
 *
 * Module-level, like the registry it reads: `STUDIO_ROUTE_REGISTRY` is
 * filtered once at load from a Vite build-time env var, so a route withheld
 * from this distribution is absent from the palette without a second
 * conditional here.
 */
export const STUDIO_COMMAND_DESTINATIONS: readonly StudioCommandDestination[] = [
  ...STUDIO_GLOBAL_NAV_SECTIONS.flatMap((section) =>
    section.routes.map((route) => ({ route, section: section.id })),
  ),
  // Palette destinations derive from the same ownership rows as the rail.
  // Contextual Settings and privileged Admin retain their dedicated palette
  // entries without making every contextual route a global destination.
  ...STUDIO_ROUTE_REGISTRY.filter(
    (entry) =>
      !('legacyAliasFor' in entry) &&
      ((entry.route === 'settings' && entry.navigationVisibility === 'contextual') ||
        (entry.route === 'admin' && entry.navigationVisibility === 'privileged')),
  ).map((entry) => ({ route: entry.route, section: null })),
]
